import { PlusIcon } from "@xola/icons";
import { flash, Popover, SubmitButton } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useState } from "react";
import { useMutation } from "react-query";
import {
    AllowedPrivacy,
    BaseScheduleType,
    Experience,
    LineItemTemplate,
    PricingGroupSchedules,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkTag,
    Schedule,
    SelectionState,
} from "types";
import { api } from "../../../api/api";
import { useCollection } from "../../../hooks/useCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import {
    generatePurchaseRulesFromPriceVariation,
    getSchedulePurchaseRuleLinkPayload,
} from "../../../models/PriceVariation";
import { getPurchaseRulesByTag } from "../../../models/PurchaseRule";
import { getPurchaseRulesFromPurchaseRuleLink } from "../../../models/PurchaseRuleLink";
import { getAllowedPrivaciesForAvailableSchedule, getDefaultScheduleForExperience } from "../../../models/Schedule";
import { PriceVariationPayload } from "../../../types/PriceVariationPayload";
import { ScheduleSelector } from "../../Common/ScheduleSelector";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ScheduleEditorModal } from "../SchedulesAvailability/ScheduleEditorModal";

interface AddSchedulePopoverProps {
    value: PricingGroupSchedules;
    experience: Experience;
    onSubmit: (value: PricingGroupSchedules) => void;
}

export const AddSchedulePopover = ({ value, experience, onSubmit }: AddSchedulePopoverProps) => {
    const [isSchedulePopoverOpen, setIsSchedulePopoverOpen] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedSchedules, setSelectedSchedules] = useState<SelectionState>({
        all: value.all,
        selected: value.items,
    });

    const invalidate = useInvalidate();

    const { data: purchaseRuleLinks } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": PurchaseRuleLinkTag.PRICING,
        "entities.id": experience.id,
        expand: "purchaseRule",
        limit: 1,
    });

    const [purchaseRuleLink] = purchaseRuleLinks;
    const purchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);

    const { data: lineItemTemplates } = useCollection<LineItemTemplate>("lineItemTemplates");

    const toggleCreateScheduleModal = () => {
        setIsSchedulePopoverOpen(false);
        setShowCreateModal(true);
    };

    const handleSubmit = () => {
        onSubmit({ all: selectedSchedules.all, items: selectedSchedules.selected });
        setIsSchedulePopoverOpen(false);
    };

    const handleChange = (value: SelectionState) => {
        setSelectedSchedules(value);
    };

    const { mutateAsync: createSchedule } = useMutation(async (payload: Partial<Schedule>) => {
        if (payload.type === BaseScheduleType.AVAILABLE) {
            payload.allowedPrivacies = getAllowedPrivaciesForAvailableSchedule(experience);
        }
        const response = await api.post<Schedule>(`/experiences/${experience.id}/schedules`, payload);
        return response.data;
    });

    const { mutateAsync: updatePurchaseRule } = useMutation(async (payload: Partial<PurchaseRule>) => {
        if (payload?.id) {
            const { id } = payload;
            const { data } = await api.put<PurchaseRule>(`/purchaseRules/${id}`, payload);
            return data;
        }

        const { data } = await api.post<PurchaseRule>(`/purchaseRules`, payload);
        return data;
    });

    const { mutateAsync: updatePurchaseRuleLink } = useMutation(async (payload: PurchaseRuleLink) => {
        if (payload?.id) {
            const { id } = payload;
            const { data } = await api.put<PurchaseRuleLink>(`/purchaseRuleLinks/${id}`, payload);
            return data;
        }

        const { data } = await api.post<PurchaseRuleLink>(`/purchaseRuleLinks`, payload);
        return data;
    });

    const savePurchaseRules = async (updatedPurchaseRules: PurchaseRule[], purchaseRuleLink: PurchaseRuleLink) => {
        const purchaseRulePromises = updatedPurchaseRules.map(async (purchaseRule) => updatePurchaseRule(purchaseRule));

        await Promise.all(purchaseRulePromises).then(async (result) => {
            const savedPurchaseRules: PurchaseRule[] = result.map((item) => item);
            const purchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);

            for (const purchaseRule of savedPurchaseRules) {
                if (purchaseRules.length > 0) {
                    const isNewPurchaseRule = purchaseRules.some((rule) => purchaseRule.id !== rule.id);
                    if (isNewPurchaseRule) {
                        purchaseRules.push(purchaseRule);
                    }
                } else {
                    purchaseRules.push(purchaseRule);
                }
            }

            const updatedPurchaseRuleLink = await updatePurchaseRuleLink(
                getSchedulePurchaseRuleLinkPayload(experience?.seller.id, experience, purchaseRuleLink, purchaseRules),
            );
            if ("id" in updatedPurchaseRuleLink) {
                invalidate("purchaseRuleLinks", updatedPurchaseRuleLink.id);
            }

            flash.success("Successfully updated experience");
        });
    };

    const handleScheduleCreate = async (values: Schedule, priceVariationPayload?: PriceVariationPayload) => {
        const schedule = await createSchedule(values);
        if (schedule.id && priceVariationPayload?.priceVariation && priceVariationPayload.purchaseRuleLink) {
            const { priceVariation, purchaseRuleLink } = priceVariationPayload;
            const purchaseRules = generatePurchaseRulesFromPriceVariation(
                schedule.id,
                experience,
                lineItemTemplates,
                priceVariation,
                purchaseRuleLink,
            );
            savePurchaseRules(purchaseRules, purchaseRuleLink);
            invalidate("experiences", experience.id);
            invalidate("products", experience.id);
            flash.success("Successfully created schedule");
        }
    };

    const shouldShowCreateScheduleButton = () => {
        const privatePurchaseRules = getPurchaseRulesByTag(AllowedPrivacy.PRIVATE, purchaseRules);
        return privatePurchaseRules.length > 0;
    };

    return (
        <>
            <Popover
                visible={isSchedulePopoverOpen}
                maxWidth={400}
                onClickOutside={() => setIsSchedulePopoverOpen(false)}
            >
                <SecondaryButton
                    size="small"
                    icon={<PlusIcon />}
                    onClick={() => setIsSchedulePopoverOpen(!isSchedulePopoverOpen)}
                >
                    Add Schedule
                </SecondaryButton>
                <Popover.Content>
                    <h3 className="p-4">Private Pricing Available for These Schedules</h3>
                    <ScheduleSelector
                        value={selectedSchedules}
                        schedules={experience.schedules}
                        onChange={handleChange}
                    />
                    <div
                        className={clsx(
                            "flex p-4",
                            shouldShowCreateScheduleButton() ? "justify-between" : "flex-row-reverse",
                        )}
                    >
                        {shouldShowCreateScheduleButton() ? (
                            <SecondaryButton icon={<PlusIcon />} onClick={toggleCreateScheduleModal}>
                                Create Schedule
                            </SecondaryButton>
                        ) : null}
                        <div className="space-x-2">
                            <SecondaryButton onClick={() => setIsSchedulePopoverOpen(false)}>Cancel</SecondaryButton>
                            <SubmitButton onClick={handleSubmit}>Save</SubmitButton>
                        </div>
                    </div>
                </Popover.Content>
            </Popover>
            <ScheduleEditorModal
                title="Schedule"
                isOpen={showCreateModal}
                initialValues={getDefaultScheduleForExperience(experience, false)}
                blackoutSchedules={false}
                experience={experience}
                onApply={handleScheduleCreate}
                onClose={() => setShowCreateModal(false)}
            />
        </>
    );
};
